import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, createContext, useContext } from 'react';
import toast from 'react-hot-toast';
import config from '../config';
import { formatDonationNote, getShittyVaultAddress, isShittyKittiesAsset, validateDonationAssets } from '../utils/donations';
import { useAlgo } from './useAlgo';
import { useUserAssetsQuery } from './useUserAssetsStore';
import party from 'party-js';

const DonationsContext = createContext({});
export const useDonations = () => useContext(DonationsContext);

export const DonationsProvider = ({ children }) => {
  const { constructDonationTransactions, signAndSubmitDonation } = useAlgo();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const donate = async (assets, anonymous = false) => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(false);

      const { txns } = await constructDonationTransactions(assets);

      const result = await signAndSubmitDonation(txns, assets, anonymous);

      setSuccess(true);
      return result;
    } catch (error) {
      console.error('Donation error:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to complete donation';
      setError(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const clearState = () => {
    setError(null);
    setSuccess(false);
  };

  return (
    <DonationsContext.Provider
      value={{
        donate,
        loading,
        error,
        success,
        clearState,
      }}
    >
      {children}
    </DonationsContext.Provider>
  );
};

export const useDonationsHistoryQuery = ({ address, limit = 20, offset = 0 } = {}) => {
  return useQuery({
    queryKey: ['donationsHistory', address, limit, offset],
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString(),
      });

      if (address) {
        params.append('address', address);
      }

      const response = await fetch(`/api/donations/history?${params}`);
      if (!response.ok) {
        throw new Error('Failed to fetch donations history');
      }
      return response.json();
    },
    refetchOnWindowFocus: false,
    refetchInterval: 30000,
  });
};

export const useDonationSelection = () => {
  const { address } = useAlgo();
  const userAssetsQuery = useUserAssetsQuery({ address });
  const [selectedAssets, setSelectedAssets] = useState([]);
  const queryClient = useQueryClient();
  const { donate, loading, error, success, clearState } = useDonations();

  const userAssets = userAssetsQuery.data || [];

  const donatableAssets = useMemo(() => {
    return userAssets.filter((asset) => !isShittyKittiesAsset(asset));
  }, [userAssets]);

  const canDonate = useMemo(() => {
    if (!config.donationConfig.enabled) return false;
    if (!address) return false;
    if (selectedAssets.length === 0) return false;

    const errors = validateDonationAssets(selectedAssets);
    return errors.length === 0;
  }, [address, selectedAssets]);

  const donationMutate = useMutation({
    mutationFn: async ({ anonymous = false, confettiRef = null } = {}) => {
      if (!canDonate) {
        throw new Error('Cannot proceed with donation');
      }

      const assetsData = selectedAssets.map((asset) => ({
        id: asset['asset-id'],
        name: asset.params?.name || `Asset ${asset['asset-id']}`,
        image: asset.params?.url || '',
        collection: asset.params?.['creator-address'] || '',
        unitName: asset.params?.['unit-name'] || '',
      }));

      const result = await donate(assetsData, anonymous);

      return { result, confettiRef };
    },
    onSuccess: (data, variables, context) => {
      setSelectedAssets([]);
      toast.success(`Successfully donated ${selectedAssets.length} NFTs to the Shitty Vault!`);

      queryClient.invalidateQueries(['userAssets', address]);
      queryClient.invalidateQueries(['donationsHistory']);

      if (data?.confettiRef?.current) {
        party.confetti(data.confettiRef.current, {
          count: party.variation.range(20, 60),
        });
      }
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to donate NFTs');
    },
  });

  const toggleAssetSelection = (asset) => {
    const assetId = asset['asset-id'];
    const isSelected = selectedAssets.some((selected) => selected['asset-id'] === assetId);

    if (isSelected) {
      setSelectedAssets((prev) => prev.filter((selected) => selected['asset-id'] !== assetId));
    } else {
      if (selectedAssets.length >= config.donationConfig.maxAssetsPerDonation) {
        toast.error(`Maximum ${config.donationConfig.maxAssetsPerDonation} assets can be selected`);
        return;
      }
      setSelectedAssets((prev) => [...prev, asset]);
    }
  };

  const clearSelection = () => {
    setSelectedAssets([]);
  };

  return {
    donatableAssets,
    selectedAssets,
    canDonate,
    donationMutate,
    toggleAssetSelection,
    clearSelection,
    isLoading: userAssetsQuery.isLoading || donationMutate.isLoading || loading,
    userAssets,
    error,
    success,
    clearState,
  };
};
